import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { Card, Form, Button, Spinner, Row, Col, Table as BsTable } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { revenueReportSchema } from '../../../schemas/wallet/admin.schema';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import CurrencyDisplay from '../../../component/wallet/CurrencyDisplay';
import EmptyState from '../../../component/wallet/EmptyState';
import { useAdmin } from '../../../Hooks/wallet/useadmin';
import { formatDate } from '../../../lib/wallet/utils';
import { GROUP_BY_OPTIONS } from '../../../lib/wallet/constants';
import { getCurrencies } from '../../../api/wallet/wallet.api';

const RevenueReportPage = () => {
    const { sidebar } = useContext(AppContext);
    const { loading, fetchRevenueReport, fetchApiKeys } = useAdmin();
    const [currencies, setCurrencies] = useState([]);
    const [apiKeys, setApiKeys] = useState([]);
    const [report, setReport] = useState(null);

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: zodResolver(revenueReportSchema),
        defaultValues: { group_by: 'day' },
    });

    useEffect(() => {
        const load = async () => {
            try {
                const [currRes, keysRes] = await Promise.all([
                    getCurrencies(),
                    fetchApiKeys({ limit: 100 }),
                ]);
                setCurrencies(currRes?.data || []);
                setApiKeys(keysRes?.data?.api_keys || []);
            } catch {
                // handled
            }
        };
        load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onSubmit = async (data) => {
        try {
            const res = await fetchRevenueReport(data);
            setReport(res?.data || null);
        } catch {
            // handled
        }
    };

    const chartData = report?.breakdown?.map((item) => ({
        period: formatDate(item.period, 'MMM dd'),
        Credits: item.total_credits,
        Debits: item.total_debits,
        Refunds: item.total_refunds,
        'Net Revenue': item.net_revenue,
    })) || [];

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">Revenue Report</h6>
            </div>
                <div className="fade-in">
            

            {/* Query Form */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <Row className="g-3 align-items-end">
                            <Col xs={12} md={2}>
                                <Form.Group>
                                    <Form.Label>Currency</Form.Label>
                                    <Form.Select isInvalid={!!errors.currency_code} {...register('currency_code')}>
                                        <option value="">Currency</option>
                                        {currencies.map((c) => (
                                            <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col xs={6} md={2}>
                                <Form.Group>
                                    <Form.Label>Start Date</Form.Label>
                                    <Form.Control type="date" isInvalid={!!errors.period_start} {...register('period_start')} />
                                </Form.Group>
                            </Col>
                            <Col xs={6} md={2}>
                                <Form.Group>
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control type="date" isInvalid={!!errors.period_end} {...register('period_end')} />
                                </Form.Group>
                            </Col>
                            <Col xs={6} md={2}>
                                <Form.Group>
                                    <Form.Label>Group By</Form.Label>
                                    <Form.Select {...register('group_by')}>
                                        {GROUP_BY_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col xs={6} md={2}>
                                <Form.Group>
                                    <Form.Label>App Source <span className="text-muted">(opt)</span></Form.Label>
                                    <Form.Select {...register('app_source')}>
                                        <option value="">All Apps</option>
                                        {apiKeys.map((k) => (
                                            <option key={k.id} value={k.id}>
                                                {k.app_name || `Key #${k.id}`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={2}>
                                <Button type="submit" variant="primary" disabled={loading} className="w-100">
                                    {loading ? <Spinner size="sm" /> : 'Generate'}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Results */}
            {report ? (
                <>
                    {/* Summary */}
                    <Row className="g-3 mb-4">
                        {[
                            { label: 'Total Credits', value: report.summary?.total_credits, color: 'success' },
                            { label: 'Total Debits', value: report.summary?.total_debits, color: 'danger' },
                            { label: 'Total Refunds', value: report.summary?.total_refunds, color: 'warning' },
                            { label: 'Net Revenue', value: report.summary?.net_revenue, color: 'primary' },
                            { label: 'Transactions', value: report.summary?.transaction_count, color: 'info' },
                        ].map((s) => (
                            <Col xs={6} md key={s.label}>
                                <Card className="border-0 shadow-sm text-center">
                                    <Card.Body className="py-3">
                                        <div className="text-muted small">{s.label}</div>
                                        <div className={`fw-bold fs-5 text-${s.color}`}>
                                            {typeof s.value === 'number' ? s.value.toLocaleString() : s.value}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* Chart */}
                    {chartData.length > 0 && (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body>
                                <ResponsiveContainer width="100%" height={350}>
                                    <BarChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="period" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="Credits" fill="#16A34A" />
                                        <Bar dataKey="Debits" fill="#DC2626" />
                                        <Bar dataKey="Refunds" fill="#D97706" />
                                        <Bar dataKey="Net Revenue" fill="#53b7e8" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Breakdown Table */}
                    <Card className="border-0 shadow-sm">
                        <div className="table-responsive">
                            <BsTable striped hover className="align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Period</th>
                                        <th>App</th>
                                        <th>Credits</th>
                                        <th>Debits</th>
                                        <th>Refunds</th>
                                        <th>Net Revenue</th>
                                        <th>Txns</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.breakdown?.map((item, i) => (
                                        <tr key={i}>
                                            <td>{formatDate(item.period, 'MMM dd, yyyy')}</td>
                                            <td>{item.app_name || item.app_source_id}</td>
                                            <td><CurrencyDisplay amount={item.total_credits} currencyCode={report.currency_code} /></td>
                                            <td><CurrencyDisplay amount={item.total_debits} currencyCode={report.currency_code} /></td>
                                            <td><CurrencyDisplay amount={item.total_refunds} currencyCode={report.currency_code} /></td>
                                            <td><CurrencyDisplay amount={item.net_revenue} currencyCode={report.currency_code} /></td>
                                            <td>{item.transaction_count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </BsTable>
                        </div>
                    </Card>
                </>
            ) : (
                <EmptyState title="No report generated" description="Fill in the form above and click Generate." />
            )}
        </div>
        </div>
        </div>
    );
};

export default RevenueReportPage;
